import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext';

const API_URL = 'https://mines-game-api.nowfaru7.workers.dev';

const S = {
  page: { minHeight: '100vh', background: '#0f1115', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' },
  card: { background: '#1a1d24', borderRadius: '16px', padding: '40px', width: '100%', maxWidth: '440px', border: '1px solid #2a2d35' },
  title: { fontSize: '24px', fontWeight: 800, marginBottom: '8px', color: '#fff' },
  sub: { fontSize: '14px', color: '#6b7280', marginBottom: '32px' },
  label: { fontSize: '11px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px', display: 'block' },
  input: { width: '100%', background: '#0f1115', border: '1px solid #2a2d35', borderRadius: '8px', color: '#fff', fontSize: '14px', padding: '10px 12px', outline: 'none', marginBottom: '16px' },
  phraseBox: { background: '#0f1115', border: '2px solid #00e701', borderRadius: '8px', padding: '14px 16px', fontSize: '15px', color: '#00e701', fontWeight: 600, wordBreak: 'break-word', lineHeight: '1.6', marginBottom: '16px', textAlign: 'center', fontFamily: 'monospace' },
  btn: { width: '100%', background: '#00e701', color: '#000', fontWeight: 700, fontSize: '15px', border: 'none', borderRadius: '8px', height: '48px', cursor: 'pointer', transition: 'filter 0.15s' },
  toggle: { marginTop: '20px', textAlign: 'center', fontSize: '14px', color: '#6b7280' },
  toggleBtn: { background: 'none', border: 'none', color: '#00e701', cursor: 'pointer', fontWeight: 600, fontSize: '14px', padding: '0 4px' },
  error: { background: 'rgba(255,32,32,0.1)', border: '1px solid #ff2020', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', color: '#ff6666', marginBottom: '16px' },
  success: { background: 'rgba(0,231,1,0.1)', border: '1px solid #00e701', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', color: '#00e701', marginBottom: '16px' },
  info: { background: 'rgba(0,231,1,0.06)', border: '1px solid rgba(0,231,1,0.2)', borderRadius: '8px', padding: '12px 14px', fontSize: '13px', color: '#9ca3af', lineHeight: '1.6', marginBottom: '16px' },
  stepTitle: { fontSize: '13px', fontWeight: 700, color: '#fff', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' },
  stepNum: { background: '#00e701', color: '#000', borderRadius: '50%', width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 800 },
  availability: { fontSize: '12px', marginTop: '-12px', marginBottom: '16px' },
  available: { color: '#00e701' },
  taken: { color: '#ff4444' },
};

export default function Auth() {
  const [mode, setMode] = useState('login');
  const [step, setStep] = useState(1);
  const [robloxUsername, setRobloxUsername] = useState('');
  const [siteUsername, setSiteUsername] = useState('');
  const [password, setPassword] = useState('');
  const [phrase, setPhrase] = useState('');
  const [robloxUserId, setRobloxUserId] = useState(null);
  const [usernameAvailable, setUsernameAvailable] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { updateSession } = useAuth();
  const navigate = useNavigate();

  async function apiCall(action, data = {}) {
    const res = await fetch(API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action, ...data })
    });
    return res.json();
  }

  async function handleGetPhrase() {
    setError('');
    if (!robloxUsername) {
      setError('Enter your Roblox username');
      return;
    }
    setLoading(true);
    const data = await apiCall('getPhrase', { robloxUsername });
    setLoading(false);
    if (!data.success) {
      setError(data.message);
      return;
    }
    setPhrase(data.phrase);
    setRobloxUserId(data.userId);
    setStep(2);
  }

  async function handleVerify() {
    setError('');
    setLoading(true);
    const data = await apiCall('verify', { robloxUsername, phrase, userId: robloxUserId });
    setLoading(false);
    if (!data.success) {
      setError(data.message);
      return;
    }
    setStep(3);
  }

  async function checkUsername() {
    if (siteUsername.length < 3) {
      setUsernameAvailable(null);
      return;
    }
    const data = await apiCall('checkUsername', { username: siteUsername });
    setUsernameAvailable(data.available);
  }

  async function handleRegister() {
    setError('');
    if (siteUsername.length < 3) { setError('Username too short'); return; }
    if (password.length < 4) { setError('Password too short'); return; }
    setLoading(true);
    const data = await apiCall('register', { 
      robloxUsername, username: siteUsername, password, userId: robloxUserId 
    });
    setLoading(false);
    if (!data.success) {
      setError(data.message);
      return;
    }
    updateSession({ userId: data.userId, username: data.username, balance: data.balance });
    navigate('/game');
  }

  async function handleLogin() {
    setError('');
    if (!siteUsername || !password) { setError('Enter username and password'); return; }
    setLoading(true);
    const data = await apiCall('login', { username: siteUsername, password });
    setLoading(false);
    if (!data.success) {
      setError(data.message);
      return;
    }
    updateSession({ userId: data.userId, username: data.username, balance: data.balance });
    navigate('/game');
  }

  const handleModeSwitch = (newMode) => {
    setMode(newMode);
    setStep(1);
    setRobloxUsername('');
    setSiteUsername('');
    setPassword('');
    setPhrase('');
    setRobloxUserId(null);
    setUsernameAvailable(null);
    setError('');
  };

  return (
    <div style={S.page}>
      <div style={S.card}>
        <div style={S.title}>{mode === 'login' ? 'Welcome back' : 'Create Account'}</div>
        <div style={S.sub}>
          {mode === 'login' ? 'Sign in with your site username'
            : step === 1 ? 'Enter your Roblox username'
            : step === 2 ? 'Add phrase to your About Me, then verify'
            : 'Choose your username'}
        </div>

        {error && <div style={S.error}>{error}</div>}
        {mode === 'signup' && step === 2 && !error && <div style={S.success}>Got your phrase! Add it to your About Me.</div>}
        {mode === 'signup' && step === 3 && !error && <div style={S.success}>Roblox verified! Choose your username.</div>}

        {mode === 'signup' && (
          <>
            <label style={S.label}>Roblox Username</label>
            <input
              style={{ ...S.input, ...(step > 1 ? { background: '#1a1d24', cursor: 'not-allowed' } : {}) }}
              placeholder="YourRobloxUsername"
              value={robloxUsername}
              onChange={e => setRobloxUsername(e.target.value)}
              disabled={step > 1}
            />
          </>
        )}

        {mode === 'login' && (
          <>
            <label style={S.label}>Username</label>
            <input style={S.input} placeholder="YourUsername" value={siteUsername}
              onChange={e => setSiteUsername(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleLogin()} />
            <label style={S.label}>Password</label>
            <input type="password" style={S.input} placeholder="Enter password" value={password}
              onChange={e => setPassword(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleLogin()} />
          </>
        )}

        {mode === 'signup' && step === 1 && (
          <>
            <div style={S.info}>
              <strong>How it works:</strong><br />
              1. Enter your Roblox username<br />
              2. Get a unique phrase<br />
              3. Add phrase to your About Me<br />
              4. We verify automatically
            </div>
            <button style={S.btn} onClick={handleGetPhrase} disabled={loading}>
              {loading ? 'Loading...' : 'Get Verification Phrase'}
            </button>
          </>
        )}

        {mode === 'signup' && step === 2 && (
          <>
            <div style={S.stepTitle}><span style={S.stepNum}>1</span>Copy this exact phrase:</div>
            <div style={S.phraseBox}>{phrase}</div>
            <div style={S.stepTitle}><span style={S.stepNum}>2</span>Add to your About Me:</div>
            <div style={S.info}>
              1. Go to your Roblox profile<br />
              2. Click "Edit" on About Me<br />
              3. Paste the EXACT phrase above<br />
              4. Save your profile<br />
              5. Click "Verify"
            </div>
            <button style={S.btn} onClick={handleVerify} disabled={loading}>
              {loading ? 'Verifying...' : 'Verify with Roblox'}
            </button>
            <button style={{ ...S.btn, background: '#2a2d35', color: '#9ca3af', marginTop: '8px' }} onClick={() => setStep(1)}>Back</button>
          </>
        )}

        {mode === 'signup' && step === 3 && (
          <>
            <label style={S.label}>Choose Your Username</label>
            <input style={S.input} placeholder="YourUsername" value={siteUsername}
              onChange={e => { setSiteUsername(e.target.value); checkUsername(); }} />
            {siteUsername.length >= 3 && (
              <div style={{ ...S.availability, ...(usernameAvailable ? S.available : S.taken) }}>
                {usernameAvailable ? 'Username available' : 'Username taken'}
              </div>
            )}
            <label style={S.label}>Password</label>
            <input type="password" style={S.input} placeholder="At least 4 characters" value={password}
              onChange={e => setPassword(e.target.value)} />
            <button style={S.btn} onClick={handleRegister} disabled={loading || !usernameAvailable || siteUsername.length < 3}>
              {loading ? 'Creating...' : 'Create Account'}
            </button>
            <button style={{ ...S.btn, background: '#2a2d35', color: '#9ca3af', marginTop: '8px' }} onClick={() => setStep(2)}>Back</button>
          </>
        )}

        {mode === 'login' && (
          <>
            <button style={S.btn} onClick={handleLogin} disabled={loading}>
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </>
        )}

        <div style={S.toggle}>
          {mode === 'login' ? "Don't have an account?" : 'Already have an account?'}
          <button style={S.toggleBtn} onClick={() => handleModeSwitch(mode === 'login' ? 'signup' : 'login')}>
            {mode === 'login' ? 'Sign Up' : 'Sign In'}
          </button>
        </div>
      </div>
    </div>
  );
}
